import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useDriveUpload } from '@/user_files/drive/hooks/useDriveUpload'
import { kNoUploadableFilesMessage } from '@/user_files/drive/constants'
import { validateUserFilesUploadFile } from '@/user_files/utils/upload-validation'
import { isTerminalProcessingStatus } from '@/user_files/utils/processing-status'
import { useUserFilesPollingProcessor } from '@/user_files/polling/hooks/useUserFilesPollingProcessor'
import { selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import { kUppyUploadMaxFileSizeBytes } from '@/uppy-file-upload/constants'
import { getOrCreateUserFilesUploadFolderId } from '@/user_files/folders/utils/get-or-create-user-files-upload-folder-id'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'

/** Shared upload bridge for file-staging pages, including conflicts and processing polling. */
type UseStagedFilesUploadParams = {
  enabled: boolean
  matterId: string | null
  folderName: string
  sourceId: string
  stagedUserFileIds: string[]
  pendingUploadClientIds: string[]
  onFileRegistered: (clientTmpId: string) => void
  acceptedFileTypes?: Record<string, string[]>
  isFileAccepted?: (file: File) => boolean
}

function isStagedUploadFileAccepted(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

export function useStagedFilesUpload({
  enabled,
  matterId,
  folderName,
  sourceId,
  stagedUserFileIds,
  pendingUploadClientIds,
  onFileRegistered,
  acceptedFileTypes = { 'application/pdf': ['.pdf'] },
  isFileAccepted = isStagedUploadFileAccepted,
}: UseStagedFilesUploadParams) {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)
  const [uploadFolderId, setUploadFolderId] = useState<string | null>(null)
  const [preparingFolder, setPreparingFolder] = useState(false)
  const uploadFolderIdRef = useRef<string | null>(null)
  const currentMatterIdRef = useRef<string | null>(matterId)
  const [filesWaitingForUploadFolder, setFilesWaitingForUploadFolder] = useState<File[]>([])
  const submittedUploadBatchRef = useRef<File[] | null>(null)

  const fileIdsNeedingPolling = useMemo(
    () =>
      stagedUserFileIds.filter((fileId) => {
        const uploadFile = uploadFilesByServerFileId[fileId]
        if (!uploadFile) return false
        if (uploadFile.client.uploadStatus !== 'uploaded') return false
        return !isTerminalProcessingStatus(uploadFile.backend?.record.processing_status)
      }),
    [stagedUserFileIds, uploadFilesByServerFileId]
  )

  useUserFilesPollingProcessor({
    sourceId,
    fileIds: fileIdsNeedingPolling,
    enabled: enabled && fileIdsNeedingPolling.length > 0,
  })

  const {
    openUploadPicker,
    disabled: driveUploadDisabled,
    fileInputRef,
    addFiles,
    cancelUpload,
    conflictDialog,
    isPreparingFiles,
  } = useDriveUpload({
    folderId: uploadFolderId,
    isFileAccepted,
    validateUploadFile: validateUserFilesUploadFile,
    onFileRegistered: ({ clientTmpId }) => {
      onFileRegistered(clientTmpId)
    },
    onUploadCompleted: ({ folderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
    },
    onUploadFailed: ({ folderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
    },
  })

  // Guard on matterId too, not just enabled: a missing active matter must keep the upload
  // affordance disabled so prepareUploadFolder never forwards a null matterId downstream.
  const disabled = !enabled || !matterId || (Boolean(uploadFolderId) && driveUploadDisabled)

  const prepareUploadFolder = useCallback(async (): Promise<string | null> => {
    if (!enabled || !matterId) return null
    if (uploadFolderIdRef.current) return uploadFolderIdRef.current

    const requestedMatterId = matterId
    setPreparingFolder(true)
    try {
      const folderId = await getOrCreateUserFilesUploadFolderId({
        dispatch,
        folderName,
        getState: store.getState,
        matterId: requestedMatterId,
      })
      if (currentMatterIdRef.current !== requestedMatterId) return null

      uploadFolderIdRef.current = folderId
      setUploadFolderId(folderId)
      return folderId
    } catch (error) {
      console.error('Failed to prepare Matter Files upload folder:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'File could not be uploaded.',
          durationMs: kToastDurationMediumMs,
        })
      )
      return null
    } finally {
      setPreparingFolder(false)
    }
  }, [dispatch, enabled, folderName, matterId, store])

  // Reset the cached upload destination when the active matter changes.
  useEffect(() => {
    currentMatterIdRef.current = matterId
    uploadFolderIdRef.current = null
    submittedUploadBatchRef.current = null
    setFilesWaitingForUploadFolder([])
    setUploadFolderId(null)
  }, [matterId])

  // Hand files queued during folder creation to useDriveUpload once the folder id
  // has reached it through props (addFiles registers against the folderId prop).
  useEffect(() => {
    if (!uploadFolderId) return

    const files = filesWaitingForUploadFolder
    if (files.length === 0 || submittedUploadBatchRef.current === files) return
    // Upload registration can synchronously rerender before the queue state clears.
    submittedUploadBatchRef.current = files
    setFilesWaitingForUploadFolder((current) => (current === files ? [] : current))
    addFiles(files)
  }, [addFiles, uploadFolderId, filesWaitingForUploadFolder])

  const addFilesAfterUploadFolderReady = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || disabled) return

      const folderId = await prepareUploadFolder()
      if (!folderId) return

      if (uploadFolderId === folderId) {
        addFiles(files)
        return
      }

      setFilesWaitingForUploadFolder((waiting) => [...waiting, ...files])
    },
    [addFiles, disabled, prepareUploadFolder, uploadFolderId]
  )

  const addDroppedFiles = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (disabled) return
      // An empty dropped folder walks in as zero files, so nothing downstream ever sees the drop —
      // say so here rather than letting it look like the drop was ignored.
      if (acceptedFiles.length === 0 && fileRejections.length === 0) {
        dispatch(openGlobalToast({ type: GlobalToastType.WARNING, message: kNoUploadableFilesMessage, durationMs: kToastDurationMediumMs }))
        return
      }
      const rejectedFiles = fileRejections.map((fileRejection) => fileRejection.file)
      void addFilesAfterUploadFolderReady([...acceptedFiles, ...rejectedFiles])
    },
    [addFilesAfterUploadFolderReady, disabled, dispatch]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addDroppedFiles,
    accept: acceptedFileTypes,
    noClick: true,
    maxSize: kUppyUploadMaxFileSizeBytes,
    disabled,
  })

  const handleFilesPicked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return
      const files = event.target.files ? Array.from(event.target.files) : []
      event.target.value = ''
      void addFilesAfterUploadFolderReady(files)
    },
    [addFilesAfterUploadFolderReady, disabled]
  )

  // Make sure the upload folder exists, then open the OS picker by clicking the hidden
  // input directly. The input is mounted regardless of folder state, and the picked files
  // read the folder from a later render, so there is nothing to wait for.
  const open = useCallback(() => {
    if (disabled) return
    void (async () => {
      const folderId = await prepareUploadFolder()
      if (!folderId) return

      if (uploadFolderId === folderId) {
        openUploadPicker()
        return
      }

      fileInputRef.current?.click()
    })()
  }, [disabled, fileInputRef, openUploadPicker, prepareUploadFolder, uploadFolderId])

  return {
    inputAccept: Object.values(acceptedFileTypes).flat().join(','),
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    fileInputRef,
    onFilesPicked: handleFilesPicked,
    cancelUpload,
    conflictDialog,
    disabled,
    pendingUploadClientIds,
    preparing: preparingFolder || filesWaitingForUploadFolder.length > 0 || isPreparingFiles,
  }
}
